import {
  ArrowRight,
  CalendarCheck,
  Check,
  FileText,
  Receipt,
  GitCompareArrows,
  ShieldCheck,
  Users,
  Bot,
  Code2,
  SearchCheck,
  Maximize2,
  type LucideIcon,
} from "lucide-react";
import type { CSSProperties } from "react";
import { ScreenshotPreview } from "@/components/screenshot-preview";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { LocaleLink } from "@/components/locale-link";
import { useTranslation } from "@/common/providers/i18n-provider";
import { DEMO_URL, SIGN_UP_LEDGER } from "@/config/product-links";
import { productUrl, type ProductCtaSurface } from "@/lib/product-attribution";
import type { ImageSizes } from "@/lib/image-sizes";
import type { FaqKeyPairs } from "@/lib/jsonld";
import "@/styles/audience.css";

/** The only screenshots this template renders; verified against the generated catalog. */
const IMAGE_SIZES = {
  "/img/ledger/overview-example.webp": { width: 1600, height: 1000 },
  "/img/ledger/balance-sheet-apple.webp": { width: 1600, height: 1000 },
  "/img/ledger/journal-example.webp": { width: 1600, height: 1000 },
} satisfies ImageSizes;

type Crop = readonly [number, number, number, number];

function Screenshot({
  src,
  alt,
  label,
  crop,
  mobileCrop,
  priority = false,
}: {
  src: keyof typeof IMAGE_SIZES;
  alt: string;
  label: string;
  crop: Crop;
  mobileCrop: Crop;
  priority?: boolean;
}) {
  const { width, height } = IMAGE_SIZES[src];
  const vars = (rect: Crop, name: string) => ({
    [`--${name}-ratio`]: `${rect[2]} / ${rect[3]}`,
    [`--${name}-width`]: `${(width / rect[2]) * 100}%`,
    [`--${name}-left`]: `${(-rect[0] / rect[2]) * 100}%`,
    [`--${name}-top`]: `${(-rect[1] / rect[3]) * 100}%`,
  });
  return (
    <ScreenshotPreview
      className="audience-shot"
      src={src}
      alt={alt}
      width={width}
      height={height}
      label={label}
    >
      <span
        className="audience-crop"
        style={
          {
            ...vars(crop, "desktop"),
            ...vars(mobileCrop, "mobile"),
          } as CSSProperties
        }
      >
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
        />
      </span>
      <span className="audience-shot-label">
        <Maximize2 size={16} aria-hidden="true" />
        {label}
      </span>
    </ScreenshotPreview>
  );
}

interface AudienceConfig {
  /** Message-key prefix owning every string on the page. */
  prefix: string;
  surface: ProductCtaSurface;
  /** Three feature sections; the middle one is the tinted Fava feature. */
  sections: readonly [string, string, string];
  points: readonly { id: string; icon: LucideIcon }[];
  secondaryCta: string;
  hero: {
    src: keyof typeof IMAGE_SIZES;
    crop: Crop;
    mobileCrop: Crop;
  };
  /** Optional hero assurance chips under `<prefix>.hero.badge.<id>`. */
  badges?: readonly string[];
  /** Optional link closing the first section, keyed `<prefix>.<section>.link`. */
  firstSectionLink?: string;
}

const OVERVIEW_HERO = {
  src: "/img/ledger/overview-example.webp",
  crop: [280, 366, 1296, 515],
  mobileCrop: [280, 366, 540, 515],
} as const;

const BALANCE_SHEET_HERO = {
  src: "/img/ledger/balance-sheet-apple.webp",
  crop: [280, 565, 1296, 435],
  mobileCrop: [280, 565, 530, 435],
} as const;

/** Per-audience values; the template below is shared by every lander. */
const AUDIENCES = {
  couples: {
    prefix: "forCouples",
    surface: "for_couples",
    sections: ["oneLedger", "collaboration", "goals"],
    points: [
      { id: "sharedLedger", icon: FileText },
      { id: "gitHistory", icon: GitCompareArrows },
      { id: "privatePortable", icon: ShieldCheck },
      { id: "shareAdvisory", icon: Users },
    ],
    secondaryCta: "/demo",
    hero: OVERVIEW_HERO,
    badges: ["jointSeparate", "sharedReports", "private"],
    firstSectionLink: "/tracking",
  },
  professionals: {
    prefix: "forProfessionals",
    surface: "for_professionals",
    sections: ["workspace", "audit", "reports"],
    points: [
      { id: "auditable", icon: SearchCheck },
      { id: "portable", icon: FileText },
      { id: "automatable", icon: Bot },
      { id: "openFoundation", icon: Code2 },
    ],
    secondaryCta: "/docs/introduction-to-beancount",
    hero: BALANCE_SHEET_HERO,
  },
  freelancers: {
    prefix: "forFreelancers",
    surface: "for_freelancers",
    sections: ["invoicing", "taxes", "separation"],
    points: [
      { id: "oneFile", icon: FileText },
      { id: "quarterlyReady", icon: CalendarCheck },
      { id: "clientReports", icon: Receipt },
      { id: "portable", icon: ShieldCheck },
    ],
    secondaryCta: "/docs/industry-specific-setups",
    hero: OVERVIEW_HERO,
    badges: ["clients", "quarterly", "private"],
  },
  smallBusiness: {
    prefix: "forSmallBusiness",
    surface: "for_small_business",
    sections: ["books", "accountant", "ownership"],
    points: [
      { id: "correctBooks", icon: SearchCheck },
      { id: "accountantAccess", icon: Users },
      { id: "noInstall", icon: ShieldCheck },
      { id: "portable", icon: FileText },
    ],
    secondaryCta: "/compare/beancount-vs-quickbooks",
    hero: BALANCE_SHEET_HERO,
    badges: ["noLockIn", "accountantReady", "hosted"],
    firstSectionLink: "/compare/quickbooks-alternatives",
  },
} as const satisfies Record<string, AudienceConfig>;

/** The audience landers share the same reading flow and accessibility contract. */
export function AudiencePage({
  audience,
  faqs,
}: {
  audience: keyof typeof AUDIENCES;
  faqs: FaqKeyPairs;
}) {
  const { t } = useTranslation();
  const config: AudienceConfig = AUDIENCES[audience];
  const { prefix, surface, sections, points, hero, badges } = config;
  const signup = productUrl(SIGN_UP_LEDGER, surface);
  return (
    <div className={`audience-page audience-${audience}`}>
      <AppHeader />
      <main id="main">
        <section className="audience-hero">
          <div className="audience-container">
            <div className="audience-hero-copy">
              <div>
                <p className="audience-label">{t(`${prefix}.hero.eyebrow`)}</p>
                <h1>{t(`${prefix}.hero.title`)}</h1>
              </div>
              <div>
                <p className="audience-intro">
                  {t(`${prefix}.hero.description`)}
                </p>
                <div className="audience-actions">
                  <a className="audience-button audience-primary" href={signup}>
                    {t("homepage.startFree")}
                    <ArrowRight size={18} aria-hidden="true" />
                  </a>
                  <LocaleLink
                    className="audience-link"
                    to={config.secondaryCta}
                  >
                    {t(`${prefix}.hero.secondaryCta`)}
                  </LocaleLink>
                </div>
              </div>
            </div>
            <Screenshot
              src={hero.src}
              alt={t(`${prefix}.hero.image.alt`)}
              label={t(`${prefix}.image.expand`)}
              crop={hero.crop}
              mobileCrop={hero.mobileCrop}
              priority
            />
            {badges ? (
              <ul className="audience-assurances">
                {badges.map((id) => (
                  <li key={id}>
                    <Check size={17} aria-hidden="true" />
                    {t(`${prefix}.hero.badge.${id}`)}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
        {sections.map((section, index) => (
          <section
            key={section}
            className={`audience-section${index === 1 ? " audience-tint" : ""}`}
          >
            <div
              className={`audience-container ${index === 1 ? "audience-feature" : "audience-split"}`}
            >
              <div className="audience-heading">
                <p className="audience-label">
                  {t(`${prefix}.${section}.eyebrow`)}
                </p>
                <h2>{t(`${prefix}.${section}.title`)}</h2>
                <p>{t(`${prefix}.${section}.description`)}</p>
                {index === 1 ? (
                  <ul className="audience-bullets">
                    {[1, 2, 3].map((n) => (
                      <li key={n}>
                        <Check size={18} aria-hidden="true" />
                        <span>{t(`${prefix}.${section}.bullet.${n}`)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {config.firstSectionLink && index === 0 ? (
                  <LocaleLink
                    className="audience-link"
                    to={config.firstSectionLink}
                  >
                    {t(`${prefix}.${section}.link`)}
                    <ArrowRight size={17} aria-hidden="true" />
                  </LocaleLink>
                ) : null}
                {index === 1 ? (
                  <LocaleLink className="audience-link" to="/fava">
                    {t(`${prefix}.${section}.link`)}
                    <ArrowRight size={17} aria-hidden="true" />
                  </LocaleLink>
                ) : null}
              </div>
              {index === 1 ? (
                <Screenshot
                  src="/img/ledger/journal-example.webp"
                  alt={t(`${prefix}.${section}.image.2.alt`)}
                  label={t(`${prefix}.image.expand`)}
                  crop={[280, 262, 620, 338]}
                  mobileCrop={[475, 265, 410, 335]}
                />
              ) : (
                <ul className="audience-bullets">
                  {[1, 2, 3].map((n) => (
                    <li key={n}>
                      <Check size={18} aria-hidden="true" />
                      <span>{t(`${prefix}.${section}.bullet.${n}`)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        ))}
        <section className="audience-section audience-ownership">
          <div className="audience-container audience-split">
            <div className="audience-heading">
              <p className="audience-label">
                {t(`${prefix}.differentiator.eyebrow`)}
              </p>
              <h2>{t(`${prefix}.differentiator.title`)}</h2>
              <p>{t(`${prefix}.differentiator.description`)}</p>
            </div>
            <div className="audience-points">
              {points.map((point) => (
                <article key={point.id}>
                  <point.icon size={22} aria-hidden="true" />
                  <h3>{t(`${prefix}.differentiator.${point.id}.title`)}</h3>
                  <p>{t(`${prefix}.differentiator.${point.id}.body`)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section className="audience-section">
          <div className="audience-container audience-faq-layout">
            <div className="audience-heading">
              <h2>{t(`${prefix}.faq.title`)}</h2>
              <p>{t(`${prefix}.faq.description`)}</p>
            </div>
            <div className="audience-faq">
              {faqs.map(([question, answer]) => (
                <details key={question}>
                  <summary>
                    {t(question)}
                    <span aria-hidden="true">+</span>
                  </summary>
                  <p>{t(answer)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
        <section className="audience-final">
          <div className="audience-container audience-final-inner">
            <div className="audience-heading">
              <h2>{t(`${prefix}.cta.title`)}</h2>
              <p>{t(`${prefix}.cta.description`)}</p>
            </div>
            <div className="audience-actions">
              <a className="audience-button audience-primary" href={signup}>
                {t("homepage.startFree")}
                <ArrowRight size={18} aria-hidden="true" />
              </a>
              <a
                className="audience-button audience-secondary"
                href={productUrl(DEMO_URL, surface)}
              >
                {t("homepage.tryDemo")}
              </a>
            </div>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
